import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Factory, Warehouse, Stethoscope } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { LeadMagnet } from "@/components/LeadMagnet";

const CASES = [
  {
    icon: Warehouse, tag: "Logistique · Val-de-Marne, France",
    title: "-32 % de TMS en 18 mois sur une plateforme de 120 salariés.",
    context: "Un site logistique confronté à une hausse des déclarations de TMS chez les préparateurs de commandes et à un absentéisme de 9,2 %.",
    intervention: "Audit ergonomique complet des postes, déploiement de 2 exosquelettes lombaires en location, équipement de 40 postes (sièges, tables à niveau constant), formation PRAP de 85 opérateurs et accompagnement du CSE sur 6 mois.",
    result: "Indicateurs santé-sécurité en nette amélioration dès la première année, investissement amorti en 14 mois grâce à la baisse de l'absentéisme.",
    stats: [["-32 %", "de TMS déclarés"], ["9,2 % → 6,8 %", "d'absentéisme"], ["14 mois", "de retour sur investissement"], ["92 %", "d'opérateurs satisfaits"]],
    quote: "La démarche a transformé notre rapport à la prévention. Les équipes sont actrices, plus spectatrices.",
    author: "Directeur de site",
  },
  {
    icon: Factory, tag: "Agro-industrie · Douala, Cameroun",
    title: "240 opérateurs équipés et formés, -41 % d'accidents bénins en un an.",
    context: "Un industriel agroalimentaire en forte croissance souhaitait structurer sa politique sécurité pour répondre aux exigences de ses clients exportateurs et réduire les accidents sur ses lignes de production.",
    intervention: "Cartographie des risques sur 3 sites, dotation complète en EPI certifiés CE (240 opérateurs), formations SST et incendie en présentiel à Douala, mise en place d'un comité sécurité et d'indicateurs mensuels.",
    result: "Baisse immédiate des accidents bénins, qualification réussie aux audits clients internationaux et culture sécurité ancrée dans le quotidien des équipes.",
    stats: [["-41 %", "d'accidents bénins en 12 mois"], ["240", "opérateurs équipés et formés"], ["3", "sites mis en conformité"], ["100 %", "des audits clients réussis"]],
    quote: "Un partenaire qui comprend nos réalités terrain en Afrique et livre des standards internationaux.",
    author: "Responsable HSE",
  },
  {
    icon: Stethoscope, tag: "Santé & médico-social · Île-de-France",
    title: "-28 % d'accidents liés aux manutentions de résidents dans un groupe d'EHPAD.",
    context: "Un groupe de 4 établissements confronté à des arrêts de travail répétés chez les soignants, liés aux transferts et mobilisations de résidents.",
    intervention: "Diagnostic ergonomique des unités de soins, formation PRAP 2S de 110 soignants, équipement en aides techniques de transfert et accompagnement QVCT des équipes d'encadrement.",
    result: "Moins d'accidents, des soignants préservés et un absentéisme en recul durable — avec un impact direct sur la qualité d'accompagnement des résidents.",
    stats: [["-28 %", "d'AT liés aux manutentions"], ["110", "soignants formés PRAP 2S"], ["4", "établissements accompagnés"], ["-19 %", "d'absentéisme soignant"]],
    quote: "Nos équipes se sentent enfin écoutées et outillées. Les résultats parlent d'eux-mêmes.",
    author: "Directrice des soins",
  },
];

export default function References() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div data-testid="references-page" className="pt-[148px]">
      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>Références · Études de cas</p></Reveal>
        <Reveal delay={0.05}>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] max-w-4xl">
            Des résultats mesurés, secteur par secteur.
          </h1>
          <p className="mt-6 text-lg md:text-xl max-w-2xl" style={{ color: "var(--ink-2)" }}>
            Logistique, industrie, santé : découvrez comment nos clients transforment la prévention en performance, en Europe comme en Afrique francophone.
          </p>
        </Reveal>
      </section>

      {/* CASES */}
      {CASES.map((c, idx) => (
        <section key={c.tag} className={idx % 2 === 1 ? "" : undefined}
          style={idx % 2 === 1 ? { background: "var(--ink)", color: "var(--bone)" } : { background: "var(--white)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}
          data-testid={`case-${idx}`}>
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="overline mb-4 flex items-center gap-3" style={{ color: "var(--orange)" }}>
                  <c.icon size={20} /> {c.tag}
                </p>
                <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-none">{c.title}</h2>
              </Reveal>
              <Reveal delay={0.08}>
                <div className="mt-8 space-y-5 text-lg" style={{ color: idx % 2 === 1 ? "rgba(245,245,240,0.75)" : "var(--ink-2)" }}>
                  <p><strong style={{ color: idx % 2 === 1 ? "var(--bone)" : "var(--ink)" }}>Le contexte : </strong>{c.context}</p>
                  <p><strong style={{ color: idx % 2 === 1 ? "var(--bone)" : "var(--ink)" }}>Notre intervention : </strong>{c.intervention}</p>
                  <p><strong style={{ color: idx % 2 === 1 ? "var(--bone)" : "var(--ink)" }}>Le résultat : </strong>{c.result}</p>
                </div>
                <blockquote className="mt-8 pl-6 text-xl font-display font-bold leading-snug" style={{ borderLeft: "3px solid var(--orange)" }}>
                  “{c.quote}”
                  <footer className="mt-2 text-sm font-normal font-body" style={{ color: idx % 2 === 1 ? "rgba(245,245,240,0.6)" : "var(--ink-2)" }}>— {c.author}</footer>
                </blockquote>
              </Reveal>
            </div>
            <div className="lg:col-span-5 grid grid-cols-1 gap-px self-start"
              style={idx % 2 === 1
                ? { background: "rgba(245,245,240,0.15)", border: "1px solid rgba(245,245,240,0.15)" }
                : { background: "var(--border)", border: "1px solid var(--border)" }}>
              {c.stats.map(([v, l], i) => (
                <Reveal key={l} delay={i * 0.06}>
                  <div className="p-7 flex items-center gap-6" style={{ background: idx % 2 === 1 ? "var(--ink)" : "var(--bone)" }} data-testid={`case-${idx}-stat-${i}`}>
                    <p className="font-display font-black text-2xl md:text-3xl whitespace-nowrap" style={{ color: "var(--orange)" }}>{v}</p>
                    <p style={{ color: idx % 2 === 1 ? "rgba(245,245,240,0.75)" : "var(--ink-2)" }}>{l}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* LIVRE BLANC */}
      <div className="pt-20 md:pt-28"><LeadMagnet /></div>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24">
        <Reveal>
          <div className="p-10 md:p-16 flex flex-col md:flex-row items-center justify-between gap-6" style={{ background: "var(--orange)", color: "var(--bone)" }}>
            <div>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-none">Votre secteur sera le prochain.</h2>
              <p className="mt-4 text-lg md:text-xl max-w-lg" style={{ color: "rgba(245,245,240,0.9)" }}>Exposez-nous votre enjeu : un expert vous répond sous 48h avec une recommandation chiffrée.</p>
            </div>
            <Link to="/contact" data-testid="references-contact-btn" className="px-10 py-5 font-display font-bold text-lg whitespace-nowrap" style={{ background: "var(--ink)", color: "var(--bone)" }}>
              Parler à un expert
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
